/**
 * Social Authentication Hook
 *
 * Provides Google and Apple OAuth login via Firebase Authentication.
 * Handles the OAuth popup flow and exchanges Firebase token for app session.
 *
 * Firebase stays out of the initial bundle; the hook preloads it once the page is idle so the
 * popup can open inside the tap (Safari blocks it otherwise).
 */

'use client';

import { useState, useCallback, useEffect } from 'react';
import type { UserCredential } from 'firebase/auth';
import { getPreloadedSocialAuth, preloadSocialAuth } from '@/shared/lib/firebase/config';
import { captureError } from '@/shared/lib/errors/capture';
import { useAuthStore } from '@/shared/stores/auth-store';

type SocialProvider = 'google' | 'apple';

const PROVIDER_LABEL: Record<SocialProvider, string> = { google: 'Google', apple: 'Apple' };

const ERROR_MESSAGES: Record<string, string> = {
  'auth/popup-closed-by-user': 'Sign-in cancelled',
  'auth/popup-blocked': 'Pop-up was blocked. Please allow pop-ups for this site.',
  'auth/account-exists-with-different-credential':
    'An account already exists with this email using a different sign-in method.',
};

function getSocialErrorMessage(err: unknown, provider: SocialProvider): string {
  const { code, message } = (err ?? {}) as { code?: string; message?: string };
  const known = code ? ERROR_MESSAGES[code] : undefined;
  if (known) return known;
  if (message) return message;
  return `${PROVIDER_LABEL[provider]} sign-in failed`;
}

async function openPopupCold(provider: SocialProvider): Promise<UserCredential> {
  const kit = await preloadSocialAuth();
  return kit.signInWithPopup(kit.auth, kit.providers[provider]);
}

export interface SocialAuthResult {
  success: boolean;
  requires_username?: boolean;
  error?: string;
  /** The account has 2FA: no session yet, render the challenge with `challengeToken`. */
  twoFactorRequired?: boolean;
  challengeToken?: string;
}

export function useSocialAuth() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firebaseLogin = useAuthStore((state) => state.firebaseLogin);

  const handleFirebaseAuth = useCallback(
    async (result: UserCredential, provider: SocialProvider): Promise<SocialAuthResult> => {
      try {
        // Get Firebase ID token
        const idToken = await result.user.getIdToken();

        // Exchange Firebase token for app session
        const response = await firebaseLogin({
          firebase_token: idToken,
          provider,
        });

        if (response.twoFactorRequired) {
          return {
            success: false,
            twoFactorRequired: true,
            challengeToken: response.challengeToken,
          };
        }

        return {
          success: true,
          ...(response.requires_username !== undefined && {
            requires_username: response.requires_username,
          }),
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Authentication failed';
        throw new Error(message);
      }
    },
    [firebaseLogin]
  );

  useEffect(() => {
    const warm = () => {
      preloadSocialAuth().catch(() => {});
    };
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(warm);
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(warm, 200);
    return () => clearTimeout(id);
  }, []);

  const runSocialSignIn = useCallback(
    async (provider: SocialProvider): Promise<SocialAuthResult> => {
      setIsLoading(true);
      setError(null);

      try {
        // No await may run before signInWithPopup on the preloaded path: Safari
        // drops the tap's popup permission at the first one (FRONT-407).
        const kit = getPreloadedSocialAuth();
        const popup = kit
          ? kit.signInWithPopup(kit.auth, kit.providers[provider])
          : openPopupCold(provider);
        const result = await popup;
        return await handleFirebaseAuth(result, provider);
      } catch (err) {
        const code = (err as { code?: string }).code;
        if (code !== 'auth/popup-closed-by-user') {
          captureError(err, { component: 'SocialAuth', action: `${provider}-signin` });
        }
        const message = getSocialErrorMessage(err, provider);
        setError(message);
        return { success: false, error: message };
      } finally {
        setIsLoading(false);
      }
    },
    [handleFirebaseAuth]
  );

  const signInWithGoogle = useCallback(() => runSocialSignIn('google'), [runSocialSignIn]);
  const signInWithApple = useCallback(() => runSocialSignIn('apple'), [runSocialSignIn]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    signInWithGoogle,
    signInWithApple,
    isLoading,
    error,
    clearError,
  };
}
